/**
 * MEMBER PROFILE PAGE - FINAL CORRECT VERSION
 * Uses member.monthData.shares, member.monthData.loanBalance, etc.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MemberProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [member, setMember] = useState(null);
  const [balances, setBalances] = useState({
    shares: 0,
    normalLoan: 0,
    schoolLoan: 0,
    emergencyLoan: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const formatDate = (dateValue) => {
    if (!dateValue) return 'Unknown';
    try {
      let date;
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else {
        date = new Date(dateValue);
      }
      
      if (isNaN(date.getTime())) {
        return 'Unknown';
      }
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Unknown';
    }
  };

  useEffect(() => {
    // Don't try to fetch if id is a route keyword
    if (id === 'add' || id === 'new' || id === 'import' || id === 'manage-shares') {
      setError('Invalid member ID. Please check your routing configuration.');
      setIsLoading(false);
      return;
    }
    fetchMemberData();
  }, [id]);

  const fetchMemberData = async () => {
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const memberResponse = await fetch(`${API_URL}/members/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!memberResponse.ok) throw new Error('Failed to fetch member');

      const memberData = await memberResponse.json();
      setMember(memberData.data);

      // Fetch balances from monthly overview
      await fetchBalancesFromMonthlyOverview(memberData.data.memberId, token);
      
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to load member data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBalancesFromMonthlyOverview = async (memberId, token) => {
    try {
      const now = new Date();
      // Use previous month — current month contributions are not yet made
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const currentMonth = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;

      const response = await fetch(
        `${API_URL}/reports/monthly-overview?month=${currentMonth}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data && data.data.members) {
          // Find member in the members array
          const memberData = data.data.members.find(m => m.memberId === memberId);
          
          if (memberData && memberData.monthData) {
            // ✅ CORRECT: Access balances from monthData
            const md = memberData.monthData;
            setBalances({
              shares: md.shares || 0,
              normalLoan: md.loanBalance || 0,
              schoolLoan: md.schoolFeesLoan || 0,
              emergencyLoan: md.emergencyLoan || 0
            });
            console.log('✅ Balances loaded:', {
              shares: md.shares,
              normalLoan: md.loanBalance,
              schoolLoan: md.schoolFeesLoan,
              emergencyLoan: md.emergencyLoan
            });
          } else {
            console.warn('Member or monthData not found, using fallback');
            setBalancesFromMemberDocument();
          }
        } else {
          console.warn('Invalid response structure, using fallback');
          setBalancesFromMemberDocument();
        }
      } else {
        console.warn('Monthly overview failed, using fallback');
        setBalancesFromMemberDocument();
      }
    } catch (error) {
      console.error('Error fetching balances:', error);
      setBalancesFromMemberDocument();
    }
  };

  const setBalancesFromMemberDocument = () => {
    if (!member) return;
    
    const cb = member.currentBalances || {};
    setBalances({
      shares: cb.shares || 0,
      normalLoan: cb.normalLoanBalance || cb.normalLoan || 0,
      schoolLoan: cb.schoolLoanBalance || cb.schoolLoan || cb.schoolFeesLoan || 0,
      emergencyLoan: cb.emergencyLoanBalance || cb.emergencyLoan || 0
    });
  };

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="spinner-large"></div>
        <p>Loading member profile...</p>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="error-page">
        <h2>Error</h2>
        <p>{error || 'Member not found'}</p>
        <button className="btn btn-secondary" onClick={() => navigate('/admin/members')}>
          Back to Members
        </button>
      </div>
    );
  }

  return (
    <div className="member-profile-page">
      {/* Header */}
      <div className="profile-header">
        <button className="btn btn-secondary" onClick={() => navigate('/admin/members')}>
          ← Back to Members
        </button>
        <div className="profile-title">
          <h1>{member.personalInfo?.fullName || 'Unknown Member'}</h1>
          <span className={`status-badge status-${member.saccoInfo?.status?.toLowerCase()}`}>
            {member.saccoInfo?.status || 'Unknown'}
          </span>
        </div>
        <div className="profile-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate(`/admin/members/${member.memberId}/edit`)}
          >
            ✏️ Edit Profile
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => navigate('/admin/apply-loan', { state: { memberId: member.memberId } })}
          >
            💰 Apply for Loan
          </button>
        </div>
      </div>

      {/* Member ID Card */}
      <div className="member-card">
        <div className="card-info">
          <div className="info-item">
            <span className="label">Member ID:</span>
            <span className="value">{member.memberId}</span>
          </div>
          <div className="info-item">
            <span className="label">Phone:</span>
            <span className="value">{member.personalInfo?.phoneNumber || 'Not provided'}</span>
          </div>
          <div className="info-item">
            <span className="label">Email:</span>
            <span className="value">{member.personalInfo?.email || 'Not provided'}</span>
          </div>
          <div className="info-item">
            <span className="label">Joined:</span>
            <span className="value">{formatDate(member.saccoInfo?.dateJoined)}</span>
          </div>
        </div>
      </div>

      {/* Balance Summary Cards */}
      <div className="balance-cards">
        <div className="balance-card shares">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <div className="card-label">Share Balance</div>
            <div className="card-value">
              {balances.shares.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <div className="balance-card loan normal">
          <div className="card-icon">🏦</div>
          <div className="card-content">
            <div className="card-label">Normal Loan</div>
            <div className="card-value">
              {balances.normalLoan.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <div className="balance-card loan school">
          <div className="card-icon">🎓</div>
          <div className="card-content">
            <div className="card-label">School Loan</div>
            <div className="card-value">
              {balances.schoolLoan.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <div className="balance-card loan emergency">
          <div className="card-icon">🚨</div>
          <div className="card-content">
            <div className="card-label">Emergency Loan</div>
            <div className="card-value">
              {balances.emergencyLoan.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="overview-container">
        <div className="info-sections">
          {/* Personal Information */}
          <div className="info-section">
            <h3>Personal Information</h3>
            <div className="info-grid">
              <div className="info-row">
                <span className="info-label">Full Name:</span>
                <span className="info-value">{member.personalInfo?.fullName || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">ID Number:</span>
                <span className="info-value">{member.personalInfo?.idNumber || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Phone Number:</span>
                <span className="info-value">{member.personalInfo?.phoneNumber || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Email:</span>
                <span className="info-value">{member.personalInfo?.email || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Date of Birth:</span>
                <span className="info-value">{formatDate(member.personalInfo?.dateOfBirth)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Gender:</span>
                <span className="info-value">{member.personalInfo?.gender || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Physical Address:</span>
                <span className="info-value">{member.personalInfo?.physicalAddress || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">City / Town:</span>
                <span className="info-value">{member.personalInfo?.city || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Home County:</span>
                <span className="info-value">{member.personalInfo?.homeCounty || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Employment Status:</span>
                <span className="info-value">{member.personalInfo?.employmentStatus || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Employer:</span>
                <span className="info-value">{member.personalInfo?.employerName || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* SACCO Information */}
          <div className="info-section">
            <h3>SACCO Information</h3>
            <div className="info-grid">
              <div className="info-row">
                <span className="info-label">Member ID:</span>
                <span className="info-value">{member.memberId}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Date Joined:</span>
                <span className="info-value">{formatDate(member.saccoInfo?.dateJoined)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Membership Type:</span>
                <span className="info-value">{member.saccoInfo?.membershipType || 'Regular'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Status:</span>
                <span className={`status-badge status-${member.saccoInfo?.status?.toLowerCase()}`}>
                  {member.saccoInfo?.status || 'N/A'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Initial Shares:</span>
                <span className="info-value">KES {(member.saccoInfo?.initialShares || 0).toLocaleString()}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Monthly Commitment:</span>
                <span className="info-value">KES {(member.saccoInfo?.monthlyShareCommitment || 0).toLocaleString()}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Department:</span>
                <span className="info-value">{member.saccoInfo?.department || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Payroll Number:</span>
                <span className="info-value">{member.saccoInfo?.payrollNumber || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Next of Kin */}
          <div className="info-section">
            <h3>Next of Kin</h3>
            <div className="info-grid">
              <div className="info-row">
                <span className="info-label">Name:</span>
                <span className="info-value">{member.nextOfKin?.name || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Relationship:</span>
                <span className="info-value">{member.nextOfKin?.relationship || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Phone Number:</span>
                <span className="info-value">{member.nextOfKin?.phoneNumber || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">ID Number:</span>
                <span className="info-value">{member.nextOfKin?.idNumber || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Address:</span>
                <span className="info-value">{member.nextOfKin?.physicalAddress || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Benefit %:</span>
                <span className="info-value">{member.nextOfKin?.benefitPercentage !== undefined && member.nextOfKin?.benefitPercentage !== '' ? `${member.nextOfKin.benefitPercentage}%` : 'N/A'}</span>
              </div>
            </div>
            {/* Other Next of Kin */}
            {Array.isArray(member.otherNextOfKin) && member.otherNextOfKin.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <strong style={{ fontSize: 14 }}>Other Beneficiaries:</strong>
                <div style={{ marginTop: 6 }}>
                  {member.otherNextOfKin.map((k, i) => (
                    <div key={i} className="info-row" style={{ paddingLeft: 8, borderLeft: '3px solid #e0e0e0', marginBottom: 6 }}>
                      <span className="info-value">{k.name} — {k.relationship || '—'}{k.benefitPercentage !== '' && k.benefitPercentage !== undefined ? ` (${k.benefitPercentage}%)` : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberProfilePage;